import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getApiCompany,
  getCompanyApiEndpoint,
  getGlobalApiEndpoint,
} from "../../config/config";
import {
  Container,
  Box,
  Stack,
  Title,
  Text,
  PasswordInput,
  Button,
  Alert,
  Image,
  Group,
  TextInput,
} from "@mantine/core";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import rNameLogo from "../../assets/Reppi.svg";

function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [token, setToken] = useState("");
  const [tokenExpired, setTokenExpired] = useState(false);
  // Company the EMAIL_FORGOT_PASSWORD token was issued for - it's what routes
  // the reset call, since there's no session yet to tell us.
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get the JWT token from URL parameters
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      readToken(tokenFromUrl);
    } else {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [searchParams]);

  const readToken = tokenToCheck => {
    try {
      // Decode JWT token without verification (just to check expiration)
      const base64Url = tokenToCheck.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );

      const decodedToken = JSON.parse(jsonPayload);

      if (decodedToken.company) {
        setCompany(decodedToken.company);
      }

      // Check if token has expired
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

      if (decodedToken.exp && decodedToken.exp < currentTime) {
        // Token is expired
        setTokenExpired(true);
        setError("");
      }
    } catch (error) {
      // If token decoding fails, assume token is invalid/expired
      setTokenExpired(true);
      setError("");
    }
  };

  const handleResendEmail = async e => {
    e.preventDefault();
    setResendLoading(true);
    setError("");
    setResendSuccess("");

    if (!email) {
      setError("Please enter your email address.");
      setResendLoading(false);
      return;
    }

    try {
      const response = await fetch(
        getGlobalApiEndpoint("/v1/user/forgot-password"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, company: company || getApiCompany() }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setResendSuccess(
          "We've sent a new password reset link to your email address. Please check your inbox."
        );
        setEmail("");
      } else {
        setError(
          data.message || "Failed to send reset email. Please try again."
        );
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const validatePasswords = () => {
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validatePasswords()) {
      return;
    }

    if (!token) {
      setError("Invalid reset token. Please request a new password reset.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        getCompanyApiEndpoint(company, "/v1/user/reset-password"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            token: token,
            password: password,
          }),
        }
      );

      await response.json();

      if (response.ok) {
        setSuccess(
          "Your password has been successfully reset. You can now log in with your new password."
        );
        setPassword("");
        setConfirmPassword("");
      } else {
        setError(
          "Failed to reset password. Please try again or request a new reset link."
        );
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-secondary)",
      }}
    >
      <Container style={{ width: "100%", maxWidth: "500px" }}>
        <Box
          bg="white"
          style={{
            borderRadius: "8px",
            boxShadow: "var(--shadow-md)",
            padding: "60px 48px",
            textAlign: "center",
          }}
        >
          <Box
            mb={40}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/home")}
          >
            <Image src={rNameLogo} alt="Reppi" h={60} w="auto" mx="auto" />
          </Box>

          {/* Token Expired View */}
          {tokenExpired ? (
            <>
              <Stack gap="md" mb={32}>
                <Title
                  order={2}
                  size="2xl"
                  fw={600}
                  c="var(--color-text-primary)"
                >
                  Link Expired
                </Title>
                <Text size="sm" c="var(--color-text-secondary)" lh={1.5}>
                  Sorry, this link is expired. Please enter your email below to
                  resend the forgot password email.
                </Text>
              </Stack>

              {/* Resend Success Alert */}
              {resendSuccess && (
                <Alert
                  icon={<IconCheck size="1rem" />}
                  title="Success!"
                  color="green"
                  mb="lg"
                  ta="left"
                >
                  {resendSuccess}
                </Alert>
              )}

              {/* Error Alert */}
              {error && (
                <Alert
                  icon={<IconAlertCircle size="1rem" />}
                  title="Error"
                  color="red"
                  mb="lg"
                  ta="left"
                >
                  {error}
                </Alert>
              )}

              {resendSuccess ? (
                <Group justify="center" mb={32}>
                  <Button size="md" onClick={handleBackToLogin}>
                    Go to Login
                  </Button>
                </Group>
              ) : (
                <Box
                  component="form"
                  onSubmit={handleResendEmail}
                  style={{ textAlign: "left" }}
                >
                  <Stack gap="md">
                    <TextInput
                      label="Email address"
                      placeholder="Enter your email address"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.currentTarget.value)}
                      required
                      size="md"
                      styles={{
                        label: {
                          fontWeight: 500,
                          marginBottom: 8,
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      size="md"
                      loading={resendLoading}
                      fullWidth
                    >
                      {resendLoading ? "Sending..." : "Resend Reset Email"}
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          ) : (
            <>
              <Stack gap="md" mb={32}>
                <Title
                  order={2}
                  size="2xl"
                  fw={600}
                  c="var(--color-text-primary)"
                >
                  Reset Your Password
                </Title>
                <Text size="sm" c="var(--color-text-secondary)" lh={1.5}>
                  Enter your new password below.
                </Text>
              </Stack>

              {/* Success Alert */}
              {success && (
                <Alert
                  icon={<IconCheck size="1rem" />}
                  title="Success!"
                  color="green"
                  mb="lg"
                  ta="left"
                >
                  {success}
                </Alert>
              )}

              {/* Error Alert */}
              {error && (
                <Alert
                  icon={<IconAlertCircle size="1rem" />}
                  title="Error"
                  color="red"
                  mb="lg"
                  ta="left"
                >
                  {error}
                </Alert>
              )}

              {success ? (
                <Group justify="center" mb={32}>
                  <Button size="md" onClick={handleBackToLogin}>
                    Go to Login
                  </Button>
                </Group>
              ) : (
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  style={{ textAlign: "left" }}
                >
                  <Stack gap="md">
                    <PasswordInput
                      label="New Password"
                      placeholder="Enter your new password"
                      value={password}
                      onChange={event => setPassword(event.currentTarget.value)}
                      required
                      size="md"
                      styles={{
                        label: {
                          fontWeight: 500,
                          marginBottom: 8,
                        },
                      }}
                    />

                    <PasswordInput
                      label="Confirm New Password"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={event =>
                        setConfirmPassword(event.currentTarget.value)
                      }
                      required
                      size="md"
                      styles={{
                        label: {
                          fontWeight: 500,
                          marginBottom: 8,
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      size="md"
                      loading={loading}
                      disabled={!token || tokenExpired}
                      fullWidth
                    >
                      {loading ? "Resetting..." : "Reset Password"}
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          )}

          <Group justify="center" mt={24}>
            <Button variant="subtle" size="sm" onClick={handleBackToLogin}>
              Back to Login
            </Button>
          </Group>
        </Box>
      </Container>
    </Box>
  );
}

export default ResetPasswordPage;
